import { Link } from '@remix-run/react';
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function MobileMenu() {
	const [mobileNavOpen, setMobileNavOpen] = useState<boolean>(false);

	const trigger = useRef<HTMLButtonElement>(null);
	const mobileNav = useRef<HTMLDivElement>(null);

	// close the mobile menu on click outside
	useEffect(() => {
		const clickHandler = ({ target }: { target: EventTarget | null }): void => {
			if (!mobileNav.current || !trigger.current) return;
			if (
				!mobileNavOpen ||
				mobileNav.current.contains(target as Node) ||
				trigger.current.contains(target as Node)
			)
				return;
			setMobileNavOpen(false);
		};
		document.addEventListener('click', clickHandler);
		return () => document.removeEventListener('click', clickHandler);
	});

	// close the mobile menu if the esc key is pressed
	useEffect(() => {
		const keyHandler = ({ keyCode }: { keyCode: number }): void => {
			if (!mobileNavOpen || keyCode !== 27) return;
			setMobileNavOpen(false);
		};
		document.addEventListener('keydown', keyHandler);
		return () => document.removeEventListener('keydown', keyHandler);
	});

	const { t } = useTranslation();

	return (
		<div className="ml-4 flex items-center md:hidden">
			{/* Hamburger button */}
			<button
				ref={trigger}
				className={`hamburger ${mobileNavOpen && 'active'}`}
				aria-controls="mobile-nav"
				aria-expanded={mobileNavOpen}
				onClick={() => setMobileNavOpen(!mobileNavOpen)}
			>
				<span className="sr-only">Menu</span>
				<svg
					className="h-5 w-5 fill-current text-slate-300 transition duration-150 ease-in-out hover:text-white"
					viewBox="0 0 20 20"
					xmlns="http://www.w3.org/2000/svg"
				>
					<rect y="2" width="20" height="2" rx="1" />
					<rect y="9" width="20" height="2" rx="1" />
					<rect y="16" width="20" height="2" rx="1" />
				</svg>
			</button>

			{/*Mobile navigation */}
			<nav
				id="mobile-nav"
				ref={mobileNav}
				className="absolute left-0 top-full z-20 w-full overflow-hidden px-4 transition-all duration-300 ease-in-out sm:px-6"
				style={
					mobileNavOpen
						? { maxHeight: mobileNav.current?.scrollHeight, opacity: 1 }
						: { maxHeight: 0, opacity: 0.8 }
				}
			>
				<ul className="rounded-lg border border-transparent px-4 py-1.5 [background:linear-gradient(theme(colors.slate.900),_theme(colors.slate.900))_padding-box,_conic-gradient(theme(colors.slate.400),_theme(colors.slate.700)_25%,_theme(colors.slate.700)_75%,_theme(colors.slate.400)_100%)_border-box]">
					<li>
						<Link
							className="flex py-1.5 text-sm font-medium text-slate-300 hover:text-white"
							to="/about"
						>
							{t('header.menu1')}
						</Link>
					</li>
					<li>
						<Link
							className="flex py-1.5 text-sm font-medium text-slate-300 hover:text-white"
							to="/roadmap"
						>
							{t('header.menu2')}
						</Link>
					</li>
					<li>
						<Link
							className="flex py-1.5 text-sm font-medium text-slate-300 hover:text-white"
							to="/contact"
						>
							{t('header.menu3')}
						</Link>
					</li>
				</ul>
			</nav>
		</div>
	);
}
